import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { catchError, from, map, mergeMap, of, throwError } from 'rxjs';
import { StandardApiResponse } from '../interfaces/standard-api-response.interface';
import { ErrorReportService } from '../services/error-report.service';
import { SILENT_HTTP_ERRORS } from './silent-http-errors';
import {
  decodeErrorBody,
  safeBackendMessage,
} from '../utils/error-message.util';

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Bad request.',
  401: 'You do not have access to view this page.',
  403: 'You do not have permission to access this resource.',
  404: 'The requested resource was not found.',
  409: 'This record was changed by another request. Reload the latest version before continuing.',
  429: 'Too many requests. Please try again shortly.',
  500: 'Something went wrong. Please contact support if this continues.',
};

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const message = inject(NzMessageService);
  const modal = inject(NzModalService);
  const notification = inject(NzNotificationService);
  const router = inject(Router);
  const errorReport = inject(ErrorReportService);
  const isTicketRequest = req.url.includes('/support/tickets');
  const requestPayload =
    typeof FormData !== 'undefined' && req.body instanceof FormData
      ? undefined
      : req.body;

  const showError = (
    text: string,
    status: number | undefined,
    responseBody: unknown,
  ): void => {
    if (req.context.get(SILENT_HTTP_ERRORS)) {
      return;
    }

    const reported =
      !isTicketRequest &&
      errorReport.notify({
        message: text,
        status,
        method: req.method,
        url: req.urlWithParams,
        requestPayload,
        responseBody,
      });

    if (!reported) {
      message.error(text);
    }
  };

  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (event.type !== HttpEventType.Response) {
        return event;
      }

      const body = (
        event as HttpResponse<Partial<StandardApiResponse<unknown>>>
      ).body;

      if (
        body?.success === false ||
        (body as { status?: unknown } | null)?.status === false
      ) {
        const text =
          safeBackendMessage(body) ?? 'The request could not be completed.';
        showError(text, body?.code, body);
        return (event as HttpResponse<unknown>).clone({
          body: { ...body, message: text },
        });
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) =>
      (typeof Blob !== 'undefined' && error.error instanceof Blob
        ? from(decodeErrorBody(error.error))
        : of(error.error)
      ).pipe(
        mergeMap((body) => {
          const fallback =
            STATUS_MESSAGES[error.status] ?? 'An unexpected error occurred.';
          const text = safeBackendMessage(body) ?? fallback;

          if (error.status === 401 || error.status === 403) {
            notification.warning('Access denied', text);

            if (error.status === 401) {
              router.navigate(['/auth/login']);
            }
          } else if (error.status === 409) {
            modal.warning({
              nzTitle: 'This record was updated elsewhere',
              nzContent: text,
              nzOkText: 'Reload latest version',
              nzOnOk: () => window.location.reload(),
            });
          } else {
            showError(text, error.status, body);
          }

          // Share the decoded, display-safe message with local error handlers as well.
          const displayBody =
            body !== null && typeof body === 'object' && !Array.isArray(body)
              ? { ...body, message: text }
              : { message: text };
          return throwError(
            () =>
              new HttpErrorResponse({
                error: displayBody,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url ?? undefined,
              }),
          );
        }),
      ),
    ),
  );
};
